import { TOPIC_GROUPS } from './content.js';
import { languageIssues } from './language.js';
import { familiaritySchema, GOALS_MAX, workContextSchema, } from './onboarding.js';
/** Why a built-in screen cannot be disabled, in the words the console shows. */
export const REQUIRED = {
    WELCOME: 'Every learner starts here, and it is the way back to sign-in.',
    INTERESTS: 'The path and the starting topic are built from these answers.',
    PATH: 'It introduces the first challenge.',
    FIRST_CHALLENGE: 'Onboarding is built around the first challenge.',
    COMPLETION: 'It closes the first challenge.',
    ACCOUNT: 'An account is required before Home.',
};
/** Each screen that reads an earlier one's answer, and why, in the console's words. */
const AFTER = [
    [
        'STARTING_FOCUS',
        'INTERESTS',
        'This screen uses the learner’s chosen interests. Place it after Choose your interests.',
    ],
    [
        'FAMILIARITY',
        'INTERESTS',
        'This screen asks about the starting topic, which comes from the interests. Place it after Choose your interests.',
    ],
    [
        'PATH',
        'INTERESTS',
        'The path is built from the learner’s interests. Place it after Choose your interests.',
    ],
    [
        'FIRST_CHALLENGE',
        'PATH',
        'The path introduces the first challenge. Place it after Your path.',
    ],
    [
        'COMPLETION',
        'FIRST_CHALLENGE',
        'This screen closes the first challenge. Place it after Your first challenge.',
    ],
];
/** The screen that collects each answer a condition can read. */
const ASKED_ON = {
    goal: 'GOAL',
    workContext: 'WORK_CONTEXT',
};
/** How many questions the shared first challenge may publish with. */
const CHALLENGE_QUESTIONS = { min: 3, max: 12, usual: 9 };
const textsOf = (step) => {
    if (step.kind === 'BUILT_IN')
        return [];
    const base = [step.title, step.body, step.button];
    if (step.kind === 'INFO')
        return base.filter((text) => text !== undefined);
    return [
        ...base,
        step.skipLabel,
        ...step.options.map((one) => one.label),
    ].filter((text) => text !== undefined);
};
/**
 * Everything wrong with a flow, by step. `challengeQuestions` is how many questions the
 * shared challenge has; the flow does not hold them, its lesson does.
 */
export function flowProblems(flow, challengeQuestions) {
    const problems = [];
    const block = (step, message) => problems.push({ step, level: 'block', message });
    const live = flow.filter((step) => step.enabled);
    const at = (id) => live.findIndex((step) => step.id === id);
    for (const step of flow) {
        if (step.kind === 'BUILT_IN' && !step.enabled && REQUIRED[step.id]) {
            block(step.id, `This screen cannot be turned off. ${REQUIRED[step.id] ?? ''}`);
        }
    }
    if (live[0]?.id !== 'WELCOME') {
        block('WELCOME', 'Welcome must be the first screen.');
    }
    if (live.at(-1)?.id !== 'ACCOUNT') {
        block('ACCOUNT', 'Create your account must be the last screen before Home.');
    }
    for (const [step, needs, message] of AFTER) {
        if (at(step) !== -1 && at(needs) !== -1 && at(step) < at(needs)) {
            block(step, message);
        }
    }
    for (const step of flow) {
        if (step.kind === 'BUILT_IN' &&
            step.id !== 'GOAL' &&
            step.limit !== undefined) {
            block(step.id, 'Only Your learning goal has a pick limit.');
        }
    }
    for (const step of live) {
        if (step.showIf !== undefined) {
            const asked = ASKED_ON[step.showIf.answer];
            if (at(asked) === -1 || at(asked) > at(step.id)) {
                block(step.id, `This screen depends on an answer from ${STEP_NAMES[asked]}. Place it after that screen, and keep that screen on.`);
            }
        }
        if (step.art?.kind === 'IMAGE' &&
            !step.art.decorative &&
            (step.art.description ?? '').trim() === '') {
            block(step.id, 'Describe the image for screen readers, or mark it decorative.');
        }
        if (step.kind === 'QUESTION') {
            if (step.options.length < 2) {
                block(step.id, 'A question needs at least two answers.');
            }
            if (new Set(step.options.map((one) => one.id)).size !== step.options.length) {
                block(step.id, 'Two answers share an id.');
            }
            if (step.optional && step.skipLabel === undefined) {
                block(step.id, 'An optional question needs skip button text.');
            }
        }
        for (const text of textsOf(step)) {
            for (const issue of languageIssues(text)) {
                problems.push({
                    step: step.id,
                    level: issue.blocks ? 'block' : 'warn',
                    message: issue.message,
                });
            }
        }
    }
    if (challengeQuestions !== undefined) {
        const { min, max, usual } = CHALLENGE_QUESTIONS;
        if (challengeQuestions < min || challengeQuestions > max) {
            block('FIRST_CHALLENGE', `The challenge has ${String(challengeQuestions)} questions; it needs ${String(min)} to ${String(max)}.`);
        }
        else if (challengeQuestions > usual) {
            problems.push({
                step: 'FIRST_CHALLENGE',
                level: 'warn',
                message: `${String(challengeQuestions)} questions may run past five minutes.`,
            });
        }
    }
    return problems;
}
/** What the console's step list calls each built-in screen. */
const STEP_NAMES = {
    WELCOME: 'Welcome',
    THEME: 'Light or dark',
    MEET_LEO: 'Meet LEO',
    GOAL: 'Your learning goal',
    INTERESTS: 'Choose your interests',
    STARTING_FOCUS: 'Your starting topic',
    WORK_CONTEXT: 'Your work',
    FAMILIARITY: 'How familiar you are',
    DAILY_PACE: 'Your learning pace',
    STREAK_INTRO: 'Streak goal intro',
    STREAK_GOAL: 'Your streak goal',
    PATH: 'Your path',
    FIRST_CHALLENGE: 'Your first challenge',
    COMPLETION: 'Challenge complete',
    REMINDER: 'Daily reminder',
    ACCOUNT: 'Create your account',
};
export const stepName = (step) => step.kind === 'BUILT_IN' ? STEP_NAMES[step.id] : step.name;
/**
 * Whether the answers lead to a starting topic after the first challenge: a picked one
 * among ready interests, or the only interest when it is ready. With no
 * interest ready the challenge is the whole start, and topics wait as "Coming soon".
 */
export function hasStart(answers, ready) {
    const interests = answers.interests ?? [];
    if (!interests.some((topic) => ready.has(topic)))
        return false;
    const choice = answers.startingTopic;
    if (choice !== undefined)
        return interests.includes(choice) && ready.has(choice);
    return interests.length === 1;
}
/**
 * Starting on a topic, picking it as an interest when it was not one: the API starts only
 * a picked interest. A new start clears familiarity, which belongs to the old one.
 */
export const startOn = (topic, interests) => ({
    interests: interests.includes(topic) ? [...interests] : [...interests, topic],
    startingTopic: topic,
    familiarity: undefined,
});
/**
 * The suggestion as the starting-focus screen offers it — only among the interests — and
 * the topic it shows selected: the learner's pick, else the offer when it can be started,
 * so the main button works at once.
 */
export function preselect(picked, suggested, interests, ready) {
    const offered = suggested !== undefined && interests.includes(suggested)
        ? suggested
        : undefined;
    const selected = picked ??
        (offered !== undefined && ready.has(offered) ? offered : undefined);
    return { offered, selected };
}
/** A goal condition holds when any picked goal is one of its goals. */
const meets = (condition, answers) => {
    if (condition === undefined)
        return true;
    const anyOf = condition.anyOf;
    if (condition.answer === 'goal') {
        return (answers.goals ?? []).some((goal) => anyOf.includes(goal));
    }
    return anyOf.includes(answers.workContext ?? '');
};
/**
 * Whether a learner with these answers sees the step. S05 asks where to start only when
 * there is a choice to make, and S07 asks how familiar the learner is with the start, so
 * it waits on one.
 */
function shows(step, answers, ready) {
    if (!step.enabled || !meets(step.showIf, answers))
        return false;
    const interests = answers.interests ?? [];
    if (step.id === 'STARTING_FOCUS') {
        return interests.length >= 2 && interests.some((topic) => ready.has(topic));
    }
    if (step.id === 'FAMILIARITY')
        return hasStart(answers, ready);
    return true;
}
function walk(flow, from, by, answers, ready) {
    let at = flow.findIndex((step) => step.id === from) + by;
    for (let step = flow[at]; step !== undefined; step = flow[(at += by)]) {
        if (shows(step, answers, ready))
            return step.id;
    }
    return from;
}
export const nextStep = (flow, from, answers, ready) => walk(flow, from, 1, answers, ready);
export const previousStep = (flow, from, answers, ready) => walk(flow, from, -1, answers, ready);
/**
 * Whether a screen's answer is saved, for the screens that always save one on the way
 * past. A skippable answer is left out: absent reads the same as skipped.
 */
const ANSWERED = {
    GOAL: (answers) => answers.goals !== undefined,
    DAILY_PACE: (answers) => answers.dailyTarget !== undefined,
    STREAK_GOAL: (answers) => answers.streakGoal !== undefined,
};
/** A screen that introduces a question, so a learner sent back to the question meets it. */
const LEADS_INTO = {
    STREAK_GOAL: 'STREAK_INTRO',
};
/**
 * The first screen before `at` the learner sees but has not answered — one added after
 * they passed its place — or `undefined` when nothing before `at` is missing.
 */
export function firstGap(flow, at, answers, ready) {
    const end = flow.findIndex((step) => step.id === at);
    const gap = flow
        .slice(0, Math.max(end, 0))
        .find((step) => step.kind === 'BUILT_IN' &&
        shows(step, answers, ready) &&
        ANSWERED[step.id]?.(answers) === false);
    if (gap?.kind !== 'BUILT_IN')
        return undefined;
    const lead = flow.find((step) => step.id === LEADS_INTO[gap.id]);
    return lead !== undefined && shows(lead, answers, ready) ? lead.id : gap.id;
}
/** The flow's first screen a learner sees, which publishing keeps as Welcome. */
export const firstStep = (flow) => flow.find((step) => step.enabled)?.id ?? 'WELCOME';
/**
 * The answer options each built-in question offers, by id, in the app's order. A flow's
 * `order` for one of these screens is read against this list: an id it does not name is
 * ignored, and one it leaves out is not shown.
 */
export const ORDERABLE = {
    GOAL: [
        'CURRENT_JOB',
        'NEXT_ROLE',
        'CAREER_CHANGE',
        'GROW_BUSINESS',
        'LEAD_PEOPLE',
        'UNDERSTAND_BUSINESS',
        'MONEY_DECISIONS',
        'SOMETHING_ELSE',
    ],
    WORK_CONTEXT: workContextSchema.options,
    FAMILIARITY: familiaritySchema.options,
    DAILY_PACE: ['1', '2', '3'],
};
/** A built-in screen's options in the flow's order, or `undefined` for the app's own. */
export function orderOf(step, known) {
    if (step.kind !== 'BUILT_IN' || step.order === undefined)
        return undefined;
    const kept = step.order.filter((id) => known.includes(id));
    return kept.length === 0 ? undefined : kept;
}
/** How many goals the flow's GOAL screen lets a learner pick. */
export const goalLimit = (flow) => {
    const goal = flow.find((step) => step.id === 'GOAL');
    return (goal?.kind === 'BUILT_IN' ? goal.limit : undefined) ?? GOALS_MAX;
};
/** The goals a learner picked, without the "just exploring" marker. */
export const pickedGoals = (goals) => (goals ?? []).filter((goal) => goal !== 'EXPLORING');
/** One tap on a goal: off if picked, on if there is room, else no change. */
export function toggleGoal(goals, goal, limit) {
    if (goals.includes(goal))
        return goals.filter((one) => one !== goal);
    return goals.length < limit ? [...goals, goal] : undefined;
}
const mapped = (goal) => goal !== 'SOMETHING_ELSE' && goal !== 'EXPLORING';
/**
 * The topics the picked goals suggest, best first: one more goals suggest ranks higher,
 * and a tie goes to the earlier pick, then to the table's order for that goal.
 */
export function goalMatches(suggestions, goals) {
    const matches = new Map();
    for (const goal of (goals ?? []).filter(mapped)) {
        const row = suggestions.find((one) => one.goal === goal);
        for (const topic of row?.topics ?? []) {
            const match = matches.get(topic) ?? { topic, goals: [] };
            match.goals.push(goal);
            matches.set(topic, match);
        }
    }
    return [...matches.values()].sort((a, b) => b.goals.length - a.goals.length);
}
/** The topics the answers' goals suggest, best first, as the live config pairs them. */
export function suggestionsFor(suggestions, answers) {
    return goalMatches(suggestions, answers.goals).map((match) => match.topic);
}
const TOPIC_ORDER = TOPIC_GROUPS.flatMap((group) => group.topics);
/**
 * The goals' suggestions first, as `goalMatches` ranks them, then onboarding's own
 * order. Never the order the learner tapped topics in — the spec rules that out as a
 * hidden ranking.
 */
export function rankByGoals(topics, goals, { suggestions }) {
    const suggested = goalMatches(suggestions, goals).map((match) => match.topic);
    const rank = (topic) => {
        const index = suggested.indexOf(topic);
        return index === -1 ? suggested.length + TOPIC_ORDER.indexOf(topic) : index;
    };
    return [...topics].sort((a, b) => rank(a) - rank(b));
}
/**
 * The topic the starting-focus screen preselects: the best ready interest, else the best
 * ready topic of all — the owner's answer for a learner none of whose interests is ready
 * yet. The API decides with this, and the console preview, which has no server, ranks
 * the same way.
 */
export function suggestStart(interests, goals, source, ready) {
    const readyInterests = interests.filter((topic) => ready.has(topic));
    return (rankByGoals(readyInterests, goals, source)[0] ??
        rankByGoals(ready, goals, source)[0] ??
        null);
}
/**
 * The screen a copy key belongs to: its first segment, `welcome` of `welcome.title`. A
 * key with none — `back`, `continue` — is shared by every screen.
 */
export const screenOf = (key) => key.includes('.') ? (key.split('.')[0] ?? key) : 'shared';
/** The copy screens each built-in step draws its words from. */
export const STEP_COPY = {
    WELCOME: ['welcome'],
    THEME: ['theme'],
    MEET_LEO: ['meetLeo'],
    GOAL: ['goal'],
    INTERESTS: ['interests'],
    STARTING_FOCUS: ['focus'],
    WORK_CONTEXT: ['work'],
    FAMILIARITY: ['familiarity'],
    DAILY_PACE: ['pace'],
    STREAK_INTRO: ['streakIntro'],
    STREAK_GOAL: ['streakGoal'],
    PATH: ['preparing', 'path', 'pathTitle'],
    FIRST_CHALLENGE: ['challenge'],
    COMPLETION: ['complete'],
    REMINDER: ['reminder'],
    ACCOUNT: ['account'],
};
//# sourceMappingURL=onboarding-flow.js.map